"use client";
import type { ReactElement } from "react";
import { motion, type MotionValue } from "framer-motion";
import { PERIOD_SPACING } from "./timeline-constants";
import type { TimelinePeriod as TimelinePeriodType } from "./timeline-types";
import { TimelineEventStack } from "./TimelineEventStack";
import { TimelinePeriodLabel } from "./TimelinePeriodLabel";
import { TimelinePeriodNode } from "./TimelinePeriodNode";
import { TimelinePeriodWash } from "./TimelinePeriodWash";
import { selectTimelineEventStackStyle, selectTimelineEventStackVisible, selectTimelineLabelTop, } from "./timeline-period-selectors";
import type { TimelineDotState } from "./timeline-period-selectors";
import { useTimelinePeriodMotionModel } from "./useTimelinePeriodMotionModel";
import type { TimelineEventHoverPayload } from "./timeline-period-types";
export type { TimelineEventHoverPayload };
export interface TimelinePeriodProps {
    period: TimelinePeriodType;
    sectionColor: string;
    isAbove: boolean;
    isCollapsed: boolean;
    isSnapped: boolean;
    dotState: TimelineDotState;
    needleActive: boolean;
    needleX: MotionValue<number>;
    trackY: number;
    chartHeight: number;
    onHoverEvent: (payload: TimelineEventHoverPayload | null) => void;
}
export function TimelinePeriod({ period, sectionColor, isAbove, isCollapsed, isSnapped, dotState, needleActive, needleX, trackY, chartHeight, onHoverEvent, }: TimelinePeriodProps): ReactElement {
    const motionModel = useTimelinePeriodMotionModel({
        sectionColor,
        needleActive,
        needleX,
        periodGlobalIndex: period.globalIndex,
        chartHeight,
        isSnapped,
    });
    const hasVisibleEvents = selectTimelineEventStackVisible({
        isCollapsed,
        eventCount: period.events.length,
    });
    const labelTop = selectTimelineLabelTop({ isAbove, trackY });
    const stackStyle = selectTimelineEventStackStyle({
        isAbove,
        chartHeight,
        trackY,
    });
    return (
      <motion.div onMouseLeave={() => {
            onHoverEvent(null);
        }} style={{
            position: "relative",
            width: PERIOD_SPACING,
            minWidth: PERIOD_SPACING,
            height: chartHeight,
            flexShrink: 0,
            opacity: motionModel.periodOpacity,
        }}>
        <TimelinePeriodWash trackY={trackY} washGradient={motionModel.washGradient} washHeight={motionModel.washHeight} washOpacity={motionModel.washOpacity}/>

        <TimelinePeriodLabel isVisible={!isCollapsed} label={period.label} labelTop={labelTop} sectionColor={sectionColor}/>

        <TimelineEventStack cardTextColor={motionModel.cardTextColor} isAbove={isAbove} isVisible={hasVisibleEvents} onHoverEvent={onHoverEvent} period={period} stackStyle={stackStyle}/>

        <TimelinePeriodNode dotState={dotState} nodeCoreScale={motionModel.nodeCoreScale} periodGlobalIndex={period.globalIndex} periodLabel={period.label} ringOpacity={motionModel.ringOpacity} ringScale={motionModel.ringScale} sectionColor={sectionColor} trackY={trackY}/>
      </motion.div>
    );
}
